import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { Dialog } from 'primereact/dialog'
import { TabPanel } from 'primereact/tabview'
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'

import {
  Btn,
  ConfirmDialog,
  CustomDropdown,
  CustomTabView,
  Loading,
  RouteChat,
} from '../../../../components'
import * as colors from '../../../../assets/js/colors'
import { showMessageBox } from '../../../../store/actions/system.actions'
import { errorHandler } from '../../../../services/util/error.service'
import { MapWithRoute } from '../../../../services/Maps'
import { getPermissions } from '../../../../services/localstorage/localstorage.service'
import * as DeliveryHttp from '../../../../services/api/https/deliveries/delivery.http'
import PainelMonitoramentoEntregasHttp from '../../../../services/api/https/dashboard/painel-monitoramento-entregas.http'

const PERMISSAO_DELEGAR_ROTAS = 146

const STATUS_META = [
  { id: 11, label: 'Saiu para Entrega', color: colors.gray },
  { id: 13, label: 'Em Rota', color: colors.violeta },
  { id: 14, label: 'No Cliente', color: colors.verde_escuro },
  { id: 15, label: 'Descarregando', color: colors.roxo },
  { id: 8, label: 'Entrega Total', color: colors.azul },
  { id: 12, label: 'Reentrega', color: colors.laranja },
  { id: 9, label: 'Devolução Total', color: colors.vinho },
  { id: 10, label: 'Devolução Parcial', color: colors.azul_escuro },
]

function ModalInformacoesRota({ visible, rota, onClose, onAnalistaTransferido, dispatch }) {
  const [maximized, setMaximized] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [rotaInfo, setRotaInfo] = useState(null)
  const [loadingInfo, setLoadingInfo] = useState(false)

  useEffect(() => {
    if (!visible || !rota) return

    setMaximized(false)
    setActiveIndex(0)
    setRotaInfo(null)
    fetchRotaInfo()
  }, [visible, rota])

  const fetchRotaInfo = async () => {
    try {
      setLoadingInfo(true)
      const data = await PainelMonitoramentoEntregasHttp.getRotaInfo(rota.fleet_id)
      setRotaInfo(data)
    } catch (error) {
      dispatch(showMessageBox(null, errorHandler(error)))
    } finally {
      setLoadingInfo(false)
    }
  }

  const statusChartData = useMemo(() => buildStatusChartData(rota), [rota])
  const dialogStyle = maximized
    ? { width: '100vw', height: '100vh' }
    : { width: '88vw', height: '82vh' }

  return (
    <Dialog
      className="monitoramento-rota-modal monitoramento-entrega-modal"
      header={`Informações da Rota${rota && rota.fleet_id ? ` - ${rota.fleet_id}` : ''}`}
      visible={visible}
      modal
      maximizable
      maximized={maximized}
      onMaximize={e => setMaximized(e.maximized)}
      onHide={onClose}
      style={dialogStyle}
      contentStyle={{
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
      }}
    >
      <div className="monitoramento-rota-modal__content">
        <CustomTabView activeIndex={activeIndex} onTabChange={setActiveIndex}>
          <TabPanel header="Info Rota">
            <InfoRotaTab
              loading={loadingInfo}
              rotaInfo={rotaInfo}
              statusChartData={statusChartData}
              dispatch={dispatch}
              onAnalistaTransferido={() => {
                fetchRotaInfo()
                if (onAnalistaTransferido) onAnalistaTransferido()
              }}
            />
          </TabPanel>
          <TabPanel header="Geoposicionamento">
            <GeoposicionamentoTab rota={rota} active={activeIndex === 1} />
          </TabPanel>
          <TabPanel header="Chat">
            <ChatTab
              rota={rota}
              rotaInfo={rotaInfo}
              loadingRotaInfo={loadingInfo}
              // O Dialog do PrimeReact 3 mantém o conteúdo montado quando fechado
              // (visible só esconde via CSS) — sem o visible aqui, o chat ficaria
              // conectado em segundo plano marcando mensagens como lidas.
              active={visible && activeIndex === 2}
            />
          </TabPanel>
        </CustomTabView>
      </div>
    </Dialog>
  )
}

function InfoRotaTab({ loading, rotaInfo, statusChartData, dispatch, onAnalistaTransferido }) {
  if (loading) return <CenteredLoading />
  if (!rotaInfo) {
    return <div className="monitoramento-rota-modal__empty">Dados da rota não encontrados</div>
  }

  return (
    <div className="monitoramento-rota-modal__info-tab">
      <InfoSection title="Dados do Veículo">
        <InfoGrid
          fields={[
            ['Descrição', rotaInfo.vehicle.description],
            ['Placa', rotaInfo.vehicle.badge],
            ['Ano', rotaInfo.vehicle.year],
            ['Capacidade - KG', rotaInfo.vehicle.capacity_kg],
            ['Capacidade - M³', rotaInfo.vehicle.capacity_m3],
            ['Tipo', rotaInfo.vehicle.type],
          ]}
        />
      </InfoSection>

      <InfoSection title="Dados do Motorista">
        <InfoGrid
          fields={[
            ['Nome', rotaInfo.driver.name],
            ['Telefone', rotaInfo.driver.phone],
            ['Núm. CNH', rotaInfo.driver.cnh_number],
            ['Categoria - CNH', rotaInfo.driver.cnh_type],
          ]}
        />
      </InfoSection>

      {rotaInfo.assistant && (
        <InfoSection title="Dados do Ajudante">
          <InfoGrid
            fields={[
              ['Nome', rotaInfo.assistant.name],
              ['Telefone', rotaInfo.assistant.phone],
            ]}
          />
        </InfoSection>
      )}

      <InfoSection title="Gráfico por Status de Entrega">
        <StatusPieChart data={statusChartData} />
      </InfoSection>

      <InfoSection title="Analista Responsável">
        <AnalistaRotaSection
          rotaInfo={rotaInfo}
          dispatch={dispatch}
          onAnalistaTransferido={onAnalistaTransferido}
        />
      </InfoSection>
    </div>
  )
}

function AnalistaRotaSection({ rotaInfo, dispatch, onAnalistaTransferido }) {
  const [destino, setDestino] = useState(null)
  const [confirmVisible, setConfirmVisible] = useState(false)
  const [transferindo, setTransferindo] = useState(false)

  const hasPermissaoDelegar = getPermissions().includes(PERMISSAO_DELEGAR_ROTAS)
  const analistasDestino = (rotaInfo.analistas || []).filter(
    analista => analista.analista_entrega_id !== rotaInfo.analista_entrega_id,
  )

  const onConfirmDelegacao = async () => {
    setConfirmVisible(false)
    try {
      setTransferindo(true)
      await PainelMonitoramentoEntregasHttp.delegarRotaAnalista(
        rotaInfo.fleet_id,
        destino.analista_entrega_id,
      )
      dispatch(showMessageBox(`Rota delegada para o Analista ${destino.analista_name}`))
      setDestino(null)
      if (onAnalistaTransferido) onAnalistaTransferido()
    } catch (error) {
      dispatch(showMessageBox(null, errorHandler(error)))
    } finally {
      setTransferindo(false)
    }
  }

  return (
    <div>
      <InfoGrid fields={[['Analista', rotaInfo.analista_name]]} />

      {hasPermissaoDelegar && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 10,
            marginTop: 10,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ minWidth: 260 }}>
            <CustomDropdown
              showLabel
              filter
              label="Delegar Rota para o Analista"
              optionLabel="analista_name"
              options={analistasDestino}
              value={destino}
              onChange={setDestino}
              placeholder="Selecione..."
            />
          </div>
          <Btn
            disabled={!destino || transferindo}
            isLoading={transferindo}
            onClick={() => setConfirmVisible(true)}
          >
            Delegar
          </Btn>
        </div>
      )}

      <ConfirmDialog
        title="Confirmar Delegação"
        isVisible={confirmVisible}
        content={
          destino
            ? `Delegar a Rota ${rotaInfo.fleet_id} para o Analista ${destino.analista_name}?`
            : ''
        }
        onOk={onConfirmDelegacao}
        onCancelar={() => setConfirmVisible(false)}
      />
    </div>
  )
}

function InfoSection({ title, children }) {
  return (
    <section className="monitoramento-rota-modal__section">
      <h3>{title}</h3>
      {children}
    </section>
  )
}

function InfoGrid({ fields }) {
  return (
    <div className="monitoramento-rota-modal__info-grid">
      {fields.map(([label, value]) => (
        <div key={label} className="monitoramento-rota-modal__info-field">
          <span>{label}</span>
          <strong>{formatInfoValue(value)}</strong>
        </div>
      ))}
    </div>
  )
}

function StatusPieChart({ data }) {
  const chartData = data.filter(item => item.total > 0)
  const total = data.reduce((sum, item) => sum + item.total, 0)

  return (
    <div className="monitoramento-rota-modal__chart-area">
      <div className="monitoramento-rota-modal__chart">
        {chartData.length ? (
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={chartData}
                dataKey="total"
                nameKey="label"
                innerRadius={58}
                outerRadius={96}
                paddingAngle={1}
              >
                {chartData.map(item => (
                  <Cell key={item.status_id} fill={item.color} />
                ))}
              </Pie>
              <Legend verticalAlign="bottom" layout="horizontal" align="center" />
              <Tooltip formatter={(value, name) => [`${value} entrega(s)`, name]} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="monitoramento-rota-modal__empty">Sem entregas para exibir</div>
        )}
      </div>

      <div className="monitoramento-rota-modal__status-list">
        <div className="monitoramento-rota-modal__status-total">Total: {total}</div>
        {data.map(item => (
          <div key={item.status_id} className="monitoramento-rota-modal__status-row">
            <span style={{ backgroundColor: item.color }} />
            <p>{item.label}</p>
            <strong>{item.total}</strong>
          </div>
        ))}
      </div>
    </div>
  )
}

function GeoposicionamentoTab({ rota, active }) {
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [customers, setCustomers] = useState([])
  const [geolocations, setGeolocations] = useState([])

  useEffect(() => {
    setLoading(false)
    setLoaded(false)
    setCustomers([])
    setGeolocations([])
  }, [rota ? rota.fleet_id : null])

  useEffect(() => {
    if (!rota || !rota.fleet_id || loaded || loading) return
    fetchGeolocation()
  }, [rota, loaded, loading])

  const fetchGeolocation = async () => {
    try {
      setLoading(true)
      const data = await DeliveryHttp.getGeolocationClientsByFleet(rota.fleet_id)
      const geolocationsLimited = limitarGeolocations(data.geolocations || [])
      setCustomers(data.clients || [])
      setGeolocations(geolocationsLimited.map(geo => [geo.longitude, geo.latitude]))
      setLoaded(true)
    } catch (error) {
      setCustomers([])
      setGeolocations([])
      setLoaded(true)
    } finally {
      setLoading(false)
    }
  }

  if (loading || !loaded) return <CenteredLoading />

  return (
    <div className="monitoramento-rota-modal__map-tab">
      {geolocations.length ? (
        active && (
          <div style={{ height: 560 }}>
            <MapWithRoute
              customers={customers}
              vehicleWaypoints={geolocations}
              changeLoadStatus={() => {}}
            />
          </div>
        )
      ) : (
        <div className="monitoramento-rota-modal__empty">
          Sem dados de geolocalização do veículo
        </div>
      )}
    </div>
  )
}

function ChatTab({ rota, rotaInfo, loadingRotaInfo, active }) {
  // Monta o chat apenas com a aba visível: evita conexão desnecessária e
  // marcar mensagens como lidas sem o usuário ter aberto o chat.
  if (!active) return null
  if (loadingRotaInfo) return <CenteredLoading />

  const fleetId = rotaInfo ? rotaInfo.fleet_id : rota ? rota.fleet_id : null
  if (!fleetId) {
    return (
      <div className="monitoramento-rota-modal__empty">
        Dados da rota não encontrados para carregar o chat
      </div>
    )
  }

  return (
    <div className="monitoramento-rota-modal__chat">
      <RouteChat
        fleetId={fleetId}
        title={
          rotaInfo && rotaInfo.vehicle
            ? `Chat Tp Drive - Veículo - ${rotaInfo.vehicle.badge}`
            : `Chat Tp Drive - Rota ${fleetId}`
        }
      />
    </div>
  )
}

function CenteredLoading() {
  return (
    <div className="monitoramento-rota-modal__loading">
      <Loading />
    </div>
  )
}

function buildStatusChartData(rota) {
  const counts = new Map()
  ;(rota && rota.deliveries ? rota.deliveries : []).forEach(delivery => {
    const statusId = Number(delivery.status_id)
    counts.set(statusId, (counts.get(statusId) || 0) + 1)
  })

  return STATUS_META.map(status => ({
    status_id: status.id,
    label: status.label,
    color: status.color,
    total: counts.get(status.id) || 0,
  }))
}

function limitarGeolocations(geolocations, maxItens = 150) {
  const total = geolocations.length
  if (total <= maxItens) return geolocations

  const resultado = [geolocations[0]]
  const step = (total - 2) / (maxItens - 2)

  for (let i = 1; i < maxItens - 1; i++) {
    const index = Math.round(i * step)
    resultado.push(geolocations[index])
  }

  resultado.push(geolocations[total - 1])
  return resultado
}

function formatInfoValue(value) {
  if (value === null || value === undefined || value === '') return '-'
  return value
}

export default connect()(ModalInformacoesRota)
